import {
  Calendar as AntdCalendar,
  CalendarProps,
  Col,
  GlobalToken,
  Row,
  theme,
} from "antd";
import { Button } from "../Button";
import dayjs from "dayjs";
import "dayjs/locale/nl";
import { createStyles } from "antd-style";
import { CSSProperties, useState } from "react";

dayjs.locale("nl");

// antd 6 styles its components through `--ant-*` CSS custom properties.
// React's CSSProperties deliberately omits an index signature for them
// (csstype closed typing), so each custom property needs an assertion.
const cssVar = (name: `--ant-${string}`, value: string) =>
  ({ [name]: value }) as CSSProperties;

// Styles
const styleUtils = createStyles(
  (_, { themeToken }: { themeToken: GlobalToken }) => {
    const baseItem = {
      borderRadius: 50,
      width: 36,
      height: 36,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: "pointer",
      fontSize: 14,
      fontWeight: 500,
    };

    return {
      itemSelectable: {
        ...baseItem,
        color: themeToken.colorPrimary,
        backgroundColor: themeToken.colorPrimaryBg,
        transition: "0.3s all",
        "&:hover": {
          color: themeToken.colorPrimaryBgHover,
          backgroundColor: themeToken.colorPrimary,
        },
      },
      itemSelectableOutOfView: {
        ...baseItem,
        opacity: 0.5,
      },
      itemSelected: {
        ...baseItem,
        color: themeToken.colorPrimaryBgHover,
        backgroundColor: themeToken.colorPrimary,
      },
      cellWrapper: {
        width: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      },
      headerTitle: {
        textTransform: "capitalize",
        color: "#666",
        fontSize: 16,
        fontWeight: 400,
      },
    };
  },
);

export const Calendar = (props: CalendarProps<dayjs.Dayjs>) => {
  const { token } = theme.useToken();
  const { styles } = styleUtils({ themeToken: token });

  const selectedValue = props.value ? dayjs(props.value) : null;
  const [panelDate, setPanelDate] = useState(() =>
    props.value ? dayjs(props.value) : dayjs(),
  );

  const handleSelect = (date: dayjs.Dayjs) => {
    setPanelDate(date);
    props.onSelect?.(date, { source: "date" });
  };

  // Render a single date cell
  const renderDateCell = (current: dayjs.Dayjs, info: any) => {
    if (info.type !== "date") return info.originNode;

    const isSelected = selectedValue?.isSame(current, "day") ?? false;
    const isSelectable = !props.disabledDate?.(current);

    const className = isSelected
      ? styles.itemSelected
      : isSelectable
        ? styles.itemSelectable
        : styles.itemSelectableOutOfView;

    return (
      <div className={styles.cellWrapper}>
        <div className={className}>{current.date()}</div>
      </div>
    );
  };

  // Custom header renderer
  const renderHeader = () => {
    const currentYear = dayjs().year();
    const allowedYears = [currentYear, currentYear + 1];

    const prev = panelDate.subtract(1, "month");
    const next = panelDate.add(1, "month");

    const prevDisabled = !allowedYears.includes(prev.year());
    const nextDisabled = !allowedYears.includes(next.year());

    return (
      <Row gutter={16} align="middle" style={{ marginBottom: 10 }}>
        <Col flex="auto">
          <span className={styles.headerTitle}>
            {panelDate.format("MMMM YYYY")}
          </span>
        </Col>

        <Col flex="120px" style={{ marginRight: "-16px" }}>
          <Row gutter={8} align="middle">
            <Col span={12}>
              <Button
                icon={<ArrowLeftIcon />}
                onClick={() => !prevDisabled && setPanelDate(prev)}
                disabled={prevDisabled}
                type="link"
              />
            </Col>

            <Col span={12}>
              <Button
                icon={<ArrowRightIcon />}
                onClick={() => !nextDisabled && setPanelDate(next)}
                disabled={nextDisabled}
                type="link"
              />
            </Col>
          </Row>
        </Col>
      </Row>
    );
  };

  return (
    <AntdCalendar
      {...props}
      value={panelDate}
      onSelect={handleSelect}
      headerRender={renderHeader}
      fullscreen={false}
      fullCellRender={renderDateCell}
      styles={{
        root: {
          ...cssVar("--ant-color-split", "#f2f2f2"),
          ...cssVar("--ant-color-text", "#aaa"),
          ...cssVar("--ant-padding-xs", "16px"),
        },
        content: {
          width: "108%",
          marginLeft: "-4%",
          marginRight: "-4%",
        },
        item: {
          ...cssVar("--ant-calendar-cell-bg-disabled", "transparent"),
        },
      }}
    />
  );
};

const ArrowRightIcon = () => (
  <div style={{ width: 16, height: 16, fill: "currentColor" }}>
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" width="100%">
      <path d="M218.101 38.101L198.302 57.9c-4.686 4.686-4.686 12.284 0 16.971L353.432 230H12c-6.627 0-12 5.373-12 12v28c0 6.627 5.373 12 12 12h341.432l-155.13 155.13c-4.686 4.686-4.686 12.284 0 16.971l19.799 19.799c4.686 4.686 12.284 4.686 16.971 0l209.414-209.414c4.686-4.686 4.686-12.284 0-16.971L235.071 38.101c-4.686-4.687-12.284-4.687-16.97 0z" />
    </svg>
  </div>
);

const ArrowLeftIcon = () => (
  <div style={{ width: 16, height: 16, fill: "currentColor" }}>
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" width="100%">
      <path d="M229.9 473.899l19.799-19.799c4.686-4.686 4.686-12.284 0-16.971L94.569 282H436c6.627 0 12-5.373 12-12v-28c0-6.627-5.373-12-12-12H94.569l155.13-155.13c4.686-4.686 4.686-12.284 0-16.971L229.9 38.101c-4.686-4.686-12.284-4.686-16.971 0L3.515 247.515c-4.686 4.686-4.686 12.284 0 16.971L212.929 473.9c4.686 4.686 12.284 4.686 16.971-.001z" />
    </svg>
  </div>
);
